<script setup>
	import { ref, onMounted, computed } from "vue";
	import { useWindowSize }  from "@vueuse/core";
	import { useMainStore }   from "@/store/main";
	import SuccessOrder from "./SuccessOrder.vue"
	import UserSection  from "./UserSection.vue";
	import PetSection   from "./PetSection.vue";
	import ClosePopup   from "./ClosePopup.vue";
	import { Close }    from "./Icons";
	import FastOrder    from "./FastOrder.vue";
	import Switcher     from "./ui/Switcher.vue";

	const { width } = useWindowSize();
	const mainStore = useMainStore();

	const { formData } = useMainStore();

	const hasDraft = computed(() => formData.some(el =>
		el.drafts.length &&
		!el.drafts.some(draft => !draft.time && !draft.comment)
	));

	const currentStep = ref(1);
	const isOpen      = ref(false);

	const closePopup = () => {
		isOpen.value           = false;
		mainStore.isClosePopup = false;
	}

	onMounted(() => window.addEventListener('open-popup', () => isOpen.value = true))
</script>

<template>
	<div class="form-wr" v-if="isOpen">
		<div class="form__container" v-if="!mainStore.completeOrder">
			<div class="form__menu">
				<Switcher
					v-if="width >= 768"
					v-model="currentStep"
					:values="[1, 2]"
					:disabled="!hasDraft"
				>
					<template #btn-1><span>Шаг 1</span></template>
					<template #btn-2><span>Шаг 2</span></template>
				</Switcher>
				<div class="form-menu__right">
					<button class="form__fast-order" @click="mainStore.isFastOrderPopup = true">
						Быстрая запись
					</button>
					<button
						class="form__close-btn"
						@click="mainStore.isClosePopup = true"
					>
						<Close />
					</button>
				</div>
			</div>
			<div class="form__content">
				<Switcher
					v-if="width < 768"
					v-model="currentStep"
					:values="[1, 2]"
					:disabled="!hasDraft"
				>
					<template #btn-1><span>Шаг 1</span></template>
					<template #btn-2><span>Шаг 2</span></template>
				</Switcher>
				<div class="form__label">
					<h2>Онлайн запись</h2>
					<p v-if="currentStep == 1">Введите данные вашего питомца</p>
					<p v-else>Введите ваши контактные данные</p>
				</div>
				<PetSection
					v-show="currentStep == 1"
					@nextStep="currentStep = 2"
				/>
				<UserSection v-show="currentStep == 2" />
			</div>
		</div>
		<SuccessOrder v-model="isOpen" v-else/>
		<Transition name="fade">
			<ClosePopup
				v-if="mainStore.isClosePopup"
				v-model="mainStore.isClosePopup"
				@closePopup="closePopup"
			/>
		</Transition>
		<Transition name="fade">
			<FastOrder
				v-if="mainStore.isFastOrderPopup && !mainStore.completeOrder"
				v-model="mainStore.isFastOrderPopup"
			/>
		</Transition>
	</div>
</template>

<style lang="scss">
.form-wr {
	width: 100vw;
	height: 100vh;
	display: flex;
	position: absolute;
	background-color: #0000008a;
	font-family: "Raleway";

	* { line-height: initial }

	input {
		border: none;
		padding: 0;
		padding-inline: 16px;
	}

	@include mq($large) { padding-inline: 30px }
}

.form__container {
	width: 100%;
	height: 100%;
	max-width: 1440px;
	padding: 20px 16px;
	margin: auto;
	position: relative;
	background-color: #fff;
	overflow-x: hidden;

	@include mq($tablet) { padding: 40px 32px }

	@include mq($large) {
		height: fit-content;
		max-height: 720px;
		padding: 40px;
		border-radius: 20px;
	}
}

.form__menu {
	width: 100%;
	display: flex;
	justify-content: space-between;

	.form-menu__right {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;

		@include mq($tablet) {
			width: auto;
			gap: 8px;
		}

		@include mq($tablet) { gap: 12px }
	}

	button { height: 44px }

	@include mq($large) {
		position: absolute;
		right: 40px;
		width: fit-content;

		.switcher-wr { margin-right: 12px }
	}
}

.form__fast-order {
	width: 170px;
	background-color: $yellow;
	border-radius: 12px;
	color: #fff;
	font-size: 16px;
	font-family: "Raleway";
	font-weight: 600;
}

.form {
	padding: 16px;
	background-color: #F5F5F5;

	@include mq($desktop) { padding: 24px }
}

.form__content {
	margin-top: 70px;
	display: flex;
	flex-direction: column;
	align-items: center;

	& > .switcher-wr .switcher__item {
		font-size: 16px;

		&--active {
			font-family: "Raleway";
			font-weight: 500;
		}
	}

	@include mq($tablet) { margin-top: 56px }

	@include mq($large) { margin-top: 0 }
}

.form__label {
	position: relative;
	z-index: 2;
	margin-top: 24px;
	text-align: center;

	h2 {
		font-size: 32px;
		font-family: "Raleway";
		font-weight: 600;
	}

	p {
		margin-top: 8px;
		font-family: "Raleway";
		font-weight: 500;
	}

	@include mq($tablet) {
		margin-top: 0;
	
		h2 { font-size: 36px }

		p { font-size: 20px }
	}

	@include mq($large) {
		h2 { font-size: 45px }

		p {
			margin-top: 14px;
			font-size: 24px;
		}
	}
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
